import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { storeUserInfo, clearAllAuthData, roleFromClaims } from './userInfoCache';
import { AuthTransientError } from './authErrors';
import { onLogoutBroadcast } from './crossTabLogout';

// Cognito token store: auth fully delegated to AWS Amplify — token storage,
// refresh (single-flight, rotation-aware) and revocation on sign-out are
// handled by the library. Requires configureAmplify() to have run (done by
// TenantBootstrap with the tenant's app client id).

const LOGIN_ENTRY_PATH = '/init-login';
const SIGNED_OUT_PATH = '/signed-out';
// After signOut() has pointed the browser at Cognito /logout, how long the
// page may still be alive before we assume the navigation failed.
// Long enough for a slow mobile link to reach Cognito: a later navigation
// cancels a pending one, so a short watchdog would drop the /logout request
// itself. Cancelled as soon as the page starts unloading.
const LOGOUT_REDIRECT_WATCHDOG_MS = 20000;
// fetchAuthSession() blocks while another tab has an OAuth flow in flight;
// never let that hang a sign-out.
const HAS_SESSION_TIMEOUT_MS = 5000;

// Amplify throws (after clearing the tokens) for these definitive
// authentication failures instead of returning "no tokens"; they mean
// "signed out", not "try again later".
const DEFINITIVE_AUTH_ERRORS = [
    'NotAuthorizedException',
    'TokenRevokedException',
    'RefreshTokenReuseException',
    'UserNotFoundException',
    'PasswordResetRequiredException',
    'UserNotConfirmedException',
];
const isDefinitiveAuthError = (error) =>
    DEFINITIVE_AUTH_ERRORS.some((name) => error?.name?.startsWith(name));

// Keep the synchronous user-info cache in line with the token claims
// (emitted by the Pre-Token-Generation Lambda) after each refresh.
// organizationId is NOT a claim anymore — it is fetched from the account
// endpoint at login (see login.js).
const syncUserInfoFromClaims = (claims) => {
    storeUserInfo({
        userId: claims.userId,
        role: roleFromClaims(claims),
        userName: claims.user_name,
    });
};

// ============ GET VALID ACCESS TOKEN ============
/**
 * Get a valid access token, refreshing if necessary (Amplify refreshes
 * expired tokens internally and deduplicates concurrent refreshes).
 *
 * Contract:
 *  - resolves the token when signed in;
 *  - resolves null when there is no session (never signed in, signed out,
 *    refresh token expired/revoked);
 *  - throws AuthTransientError when the token could not be obtained for a
 *    reason unrelated to the session (offline, auth service error). Callers
 *    must not sign the user out on it.
 *
 * Amplify makes the distinction for us: fetchAuthSession() returns no tokens
 * for the "no session" cases and throws for everything else.
 *
 * @param {{forceRefresh?: boolean}} [opts]
 * @returns {Promise<string|null>}
 */
export const getValidAccessToken = async ({ forceRefresh = false } = {}) => {
    let session;
    try {
        session = await fetchAuthSession({ forceRefresh });
    } catch (error) {
        if (isDefinitiveAuthError(error)) return null;
        throw new AuthTransientError(error);
    }
    const accessToken = session.tokens?.accessToken;
    if (!accessToken) return null;
    syncUserInfoFromClaims(accessToken.payload);
    return accessToken.toString();
};

// ============ LOGOUT ============
let logoutPromise = null;

const hasSession = () =>
    Promise.race([
        fetchAuthSession()
            .then((session) => !!session.tokens?.accessToken)
            // Cannot tell (e.g. offline while the token is expired): let signOut()
            // attempt the full sign-out, it tolerates its own failures.
            .catch(() => true),
        new Promise((resolve) => setTimeout(() => resolve(true), HAS_SESSION_TIMEOUT_MS)),
    ]);

/**
 * Leave the authenticated area. Always ends in a top-level navigation and
 * always settles, so react-admin never waits on it forever:
 *
 *  - session present: Amplify signOut() revokes the tokens and points the
 *    browser at Cognito /logout, which ends the hosted (SSO) session and
 *    lands back on /init-login. A watchdog covers a blocked navigation.
 *  - no session (expired / revoked / cleared): nothing for Cognito to end,
 *    go straight to /init-login. If the hosted session is still alive there,
 *    Cognito signs the user back in silently — the expected SSO behaviour for
 *    a lost local session.
 *
 * The caller (authProvider) returns `false` to react-admin so it does not
 * navigate to /login itself, which would race the redirect above.
 *
 * Idempotent — concurrent calls share the single sign-out, and the promise
 * stays settled for the remaining life of the page.
 *
 * @returns {Promise<void>}
 */
export const performLogout = async () => {
    if (logoutPromise) {
        return logoutPromise;
    }

    logoutPromise = (async () => {
        const hadSession = await hasSession();

        // Broadcasts to other tabs, which go to /signed-out (see
        // installCrossTabLogoutListener).
        clearAllAuthData();

        if (!hadSession) {
            window.location.replace(LOGIN_ENTRY_PATH);
            return;
        }

        try {
            await signOut();
        } catch {
            // Amplify not configured / unexpected failure — local state is
            // already cleared, send the user back to the login entry point.
            window.location.replace(LOGIN_ENTRY_PATH);
            return;
        }

        // signOut() has set window.location to Cognito /logout; the page is
        // unloading. If it is still here after the watchdog, the navigation
        // was blocked or failed: fall back to the login entry point.
        const watchdog = setTimeout(() => window.location.replace(LOGIN_ENTRY_PATH), LOGOUT_REDIRECT_WATCHDOG_MS);
        const cancelWatchdog = () => clearTimeout(watchdog);
        window.addEventListener('pagehide', cancelWatchdog, { once: true });
        window.addEventListener('beforeunload', cancelWatchdog, { once: true });
    })();

    return logoutPromise;
};

// ============ CROSS-TAB ============
/**
 * Another tab started signing out. Do not start a login from here: Amplify's
 * OAuth transaction is shared between tabs, so concurrent logins block or
 * invalidate each other. Go to the signed-out page (full navigation, so the
 * app stops issuing requests), where the user chooses to sign in from this
 * tab or the tab enters the app once a login completes elsewhere. Installed
 * once by the facade.
 */
export const installCrossTabLogoutListener = () =>
    onLogoutBroadcast(() => {
        if (window.location.pathname !== SIGNED_OUT_PATH) {
            window.location.replace(SIGNED_OUT_PATH);
        }
    });
